import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAddWordExportMutation } from "../../services/API/statisticsAPI";
import { selectCurrentUser } from "../../store/slices/auth/authSlice";
import Headline from "../../components/UI/Headline";
import MainArea from "../../components/UI/MainArea";
import ConvertToString from "../../components/Converter";
import generateDocx from "../../components/WordDokumente/generateDocx";
import {
  selectNumberOfPiles,
  selectPileDiameter,
  selectPileheadSource,
  selectNumberOfPileheads,
  selectTestSequenceType,
  selectNumberOfTestsForTypeDIN,
  selectNumberOfTestsForTypeExpert,
  selectTestLoadForTypeExpert,
  selectDispersionCoefficient,
  selectModelFactor,
  selectAdditionalCementConsumption,
  selectAdditionalLengths,
  selectDrillingIntoRock,
  selectDrillingThroughObstacles,
  selectDowntimesEquipmentAndWorkforce,
  selectDowntimesEquipmentOnly,
  selectDisposalDrillingSlurry,
  selectExecutionStatistics,
  selectAdditionalCementConsumptionIsChecked,
  selectAdditionalLengthsIsChecked,
  selectDrillingIntoRockIsChecked,
  selectDrillingThroughObstaclesIsChecked,
  selectDowntimesEquipmentAndWorkforceIsChecked,
  selectDowntimesEquipmentOnlyIsChecked,
  selectDisposalDrillingSlurryIsChecked,
  selectExecutionStatisticsIsChecked,
} from "../../store/slices/lvTextSlice";
import { selectBelastungsartUndRichtung } from "../../store/slices/belastungsartUndRichtungSlice";
import {
  BemessungssituationState,
  selectBemessungssituation,
  selectBerechneInnere,
} from "../../store/slices/innereSlice";
import { selectTragglied } from "../../store/slices/traggliedSlice";
import { useGetTraggliedDatenQuery } from "../../services/API/titanApi";
import { selectLongestPfahlValuePlusExtension } from "../../store/slices/aeussereSlice";
import { selectPfahlneigungZurVertikalen } from "../../store/slices/aeussereSlice";
import { selectWerkstoffUebergangsrohr } from "../../store/slices/pileHeadConstructionSlice";
import { selectBemessungslast } from "../../store/slices/bemessungslastSlice";
import { selectProjectTitle, selectProjectSubtitle } from "../../store/slices/deckblattSlice";

import { DRUCKPFAHL, ZUGPFAHL } from "../../constants";
import {
  TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE,
  TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT,
} from "../../constants";

const initialOptionsState = [
  {
    name: "optionConstructionSiteSetup",
    value: true,
    text: "createlv.optionConstructionSiteSetup",
  },
  {
    name: "optionMicropiles",
    value: true,
    text: "createlv.optionMicropiles",
  },
  {
    name: "optionPileheads",
    value: true,
    text: "createlv.optionPileheads",
  },
  {
    name: "optionTestLoads",
    value: true,
    text: "createlv.optionTestLoads",
  },
  {
    name: "optionRequirementItems",
    value: true,
    text: "createlv.optionRequirementItems",
    explanation: "createlv.optionRequirementItemsInfo",
  },
];

export default function LVExportPage() {
  const { t, i18n } = useTranslation(["translation", "fhwa"]);
  const [optionsState, setOptionsState] = useState(null);
  if (optionsState === null) {
    setOptionsState(initialOptionsState);
  }

  const curUser = useSelector(selectCurrentUser);
  const [addWordExport] = useAddWordExportMutation();

  // the complete data aggregation is done here since redux hooks are hard to accessable in functions
  const numberOfPiles = useSelector(selectNumberOfPiles);
  const loadDirection = useSelector(selectBelastungsartUndRichtung);
  const bemessungssituation = useSelector(selectBemessungssituation);
  const operationDurationHumanReadable =
    bemessungssituation === BemessungssituationState.DAUERHAFT
      ? "Belastung dauerhaft (> 2 Jahre)"
      : bemessungssituation === BemessungssituationState.TEMPORÄR
        ? "Belastung temporär (< 2 Jahre)"
        : "";

  const tragglied = useSelector(selectTragglied);
  const carryingElement = tragglied.title;

  const { data: traggliedDaten } = useGetTraggliedDatenQuery(tragglied.title);
  const innereData = useSelector(selectBerechneInnere(traggliedDaten));
  const permissibleLoadCapacity = ConvertToString(innereData?.Rcd, 0);

  const lengthWasherPlate = traggliedDaten?.kopfkonstruktion?.kantenlaenge;
  const thicknesWasherPlate = traggliedDaten?.kopfkonstruktion?.dicke;

  const totalPileLength = useSelector(selectLongestPfahlValuePlusExtension);
  const totalPileLengthOneDecimal = ConvertToString(totalPileLength, 1);
  const pileInclinationVerticle = useSelector(selectPfahlneigungZurVertikalen);
  const pileDiameter = useSelector(selectPileDiameter());

  const pileheadSource = useSelector(selectPileheadSource);
  const numberOfPileheads = useSelector(selectNumberOfPileheads);
  const transitionTubeMaterial = useSelector(selectWerkstoffUebergangsrohr, Object.is);
  const transitionTubeMaterialHumanReadable =
    transitionTubeMaterial === 0
      ? t("aeussre.plasticTube")
      : transitionTubeMaterial === 1
        ? t("aeussre.steelTube")
        : "";

  const testSequenceType = useSelector(selectTestSequenceType);
  const testSequenceTypeIsDIN =
    testSequenceType === TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE;
  const testSequenceTypeIsExpert =
    testSequenceType === TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT;
  const testSequenceTypeHumanReadable = testSequenceTypeIsDIN
    ? t("lvText.positionTestLoads.optionDINEN19971DIN1054EAPiles")
    : testSequenceTypeIsExpert
      ? t("lvText.positionTestLoads.optionSpecificationByGeotechnicalExperts")
      : "";
  const testSequenceTypeHumanReadableShort = testSequenceTypeIsDIN
    ? "DIN EN 1997-1, DIN 1054 und EA-Pfähle"
    : testSequenceTypeIsExpert
      ? "Vorgabe AG"
      : "";

  const numberOfTestsForTypeExpert = useSelector(selectNumberOfTestsForTypeExpert);
  const numberOfTestsForTypeDIN = useSelector(selectNumberOfTestsForTypeDIN);
  const numberOfTests =
    testSequenceType === TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE
      ? numberOfTestsForTypeDIN
      : testSequenceType === TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT
        ? numberOfTestsForTypeExpert
        : "";

  const testLoadForTypeExpert = useSelector(selectTestLoadForTypeExpert);
  const dispersionCoefficient = useSelector(selectDispersionCoefficient);
  const partialSafetyFactor =
    loadDirection === DRUCKPFAHL ? 1.1 : loadDirection === ZUGPFAHL ? 1.15 : "";
  const modelFactor = useSelector(selectModelFactor);
  const designLoad = useSelector(selectBemessungslast);
  const testLoadForTypeDIN =
    loadDirection === DRUCKPFAHL
      ? designLoad * partialSafetyFactor * dispersionCoefficient
      : loadDirection === ZUGPFAHL
        ? designLoad * partialSafetyFactor * dispersionCoefficient * modelFactor
        : "";
  const testLoad =
    testSequenceType === TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE
      ? testLoadForTypeDIN
      : testSequenceType === TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT
        ? testLoadForTypeExpert
        : "";

  const testLoadNoDecimals = ConvertToString(testLoad, 0);

  const additionalCementConsumption = useSelector(selectAdditionalCementConsumption);
  const additionalLengths = useSelector(selectAdditionalLengths);
  const drillingIntoRock = useSelector(selectDrillingIntoRock);
  const drillingThroughObstacles = useSelector(selectDrillingThroughObstacles);
  const downtimesEquipmentAndWorkforce = useSelector(selectDowntimesEquipmentAndWorkforce);
  const downtimesEquipmentOnly = useSelector(selectDowntimesEquipmentOnly);
  const disposalDrillingSlurry = useSelector(selectDisposalDrillingSlurry);
  const executionStatistics = useSelector(selectExecutionStatistics);

  const includeAdditionalCementConsumption = useSelector(
    selectAdditionalCementConsumptionIsChecked
  );
  const includeAdditionalLengths = useSelector(selectAdditionalLengthsIsChecked);
  const includeDrillingIntoRock = useSelector(selectDrillingIntoRockIsChecked);
  const includeDrillingThroughObstacles = useSelector(selectDrillingThroughObstaclesIsChecked);
  const includeDowntimesEquipmentAndWorkforce = useSelector(
    selectDowntimesEquipmentAndWorkforceIsChecked
  );
  const includeDowntimesEquipmentOnly = useSelector(selectDowntimesEquipmentOnlyIsChecked);
  const includeDisposalDrillingSlurry = useSelector(selectDisposalDrillingSlurryIsChecked);
  const includeExecutionStatistics = useSelector(selectExecutionStatisticsIsChecked);

  const title = useSelector(selectProjectTitle);
  const subtitle = useSelector(selectProjectSubtitle);

  const insideDiameter = carryingElement.split(" ")[1].split("/")[1];
  const outsideDiameter = carryingElement.split(" ")[1].split("/")[0];

  const totalLengthTransitionPipe =
    loadDirection === DRUCKPFAHL
      ? Number(traggliedDaten?.uebergangsrohr?.laenge_druck)
      : loadDirection === ZUGPFAHL
        ? Number(traggliedDaten?.uebergangsrohr?.laenge_zug)
        : 0;

  const lvData = {
    numberOfPiles: numberOfPiles,
    loadDirection: loadDirection,
    operationDurationHumanReadable: operationDurationHumanReadable,
    carryingElement: carryingElement,
    permissibleLoadCapacity: permissibleLoadCapacity,
    totalPileLengthOneDecimal: totalPileLengthOneDecimal,
    pileInclinationVerticle: pileInclinationVerticle,
    pileDiameter: pileDiameter,
    pileheadSource: pileheadSource,
    numberOfPileheads: numberOfPileheads,
    transitionTubeMaterialHumanReadable: transitionTubeMaterialHumanReadable,
    testSequenceTypeHumanReadable: testSequenceTypeHumanReadable,
    testSequenceTypeHumanReadableShort: testSequenceTypeHumanReadableShort,
    testLoadNoDecimals: testLoadNoDecimals,
    numberOfTests: numberOfTests,
    additionalCementConsumption: additionalCementConsumption,
    additionalLengths: additionalLengths,
    drillingIntoRock: drillingIntoRock,
    drillingThroughObstacles: drillingThroughObstacles,
    downtimesEquipmentAndWorkforce: downtimesEquipmentAndWorkforce,
    downtimesEquipmentOnly: downtimesEquipmentOnly,
    disposalDrillingSlurry: disposalDrillingSlurry,
    executionStatistics: executionStatistics,
    lengthWasherPlate: lengthWasherPlate,
    thicknesWasherPlate: thicknesWasherPlate,
    testSequenceTypeIsDIN: testSequenceTypeIsDIN,
    testSequenceTypeIsExpert: testSequenceTypeIsExpert,
    title: title,
    subtitle: subtitle,
    includeAdditionalCementConsumption: includeAdditionalCementConsumption,
    includeAdditionalLengths: includeAdditionalLengths,
    includeDrillingIntoRock: includeDrillingIntoRock,
    includeDrillingThroughObstacles: includeDrillingThroughObstacles,
    includeDowntimesEquipmentAndWorkforce: includeDowntimesEquipmentAndWorkforce,
    includeDowntimesEquipmentOnly: includeDowntimesEquipmentOnly,
    includeDisposalDrillingSlurry: includeDisposalDrillingSlurry,
    includeExecutionStatistics: includeExecutionStatistics,
    insideDiameter: insideDiameter,
    outsideDiameter: outsideDiameter,
    totalLengthTransitionPipe: totalLengthTransitionPipe,
  };

  const exportWord = async (event, state) => {
    event.preventDefault();

    const docx = await generateDocx(lvData, optionsState);
    saveAs(docx, t("lvText.mainheadline"));

    await addWordExport({
      data: {
        username: curUser,
        results: {
          carryingElement: carryingElement,
          exportAt: new Date(),
          language: i18n.language,
          selectedOptions: (optionsState ?? []).filter((o) => o.value).map((o) => o.name),
        },
      },
    });
  };

  function handleChecked(e) {
    let updatedOptionsState = optionsState.map((item) => {
      if (item.name === e.target.value) {
        return { ...item, value: !item.value };
      }
      return item;
    });
    setOptionsState(updatedOptionsState);
  }

  useEffect(() => {}, [optionsState]);

  return (
    <MainArea>
      <Headline isPrimary text={t("createlv.title")} />
      <div>
        <Row>
          <Col md={8}>
            <Row>
              <Col>
                <fieldset>
                  <h3>{t("createlv.settings")}</h3>
                  {optionsState?.map((item, index) => (
                    <div key={index}>
                      <input
                        id={item.name}
                        name={item.name}
                        type="checkbox"
                        value={item.name}
                        checked={item.value}
                        disabled={item.disabled}
                        onChange={(e) => {
                          handleChecked(e);
                        }}
                      />
                      <label htmlFor={item.name}>{t(item.text)}</label>
                      <span>{t(item.explanation)}</span>
                    </div>
                  ))}
                </fieldset>
                <button className="link-button mt-3" onClick={exportWord}>
                  {t("createlv.createWordButton")}
                </button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </MainArea>
  );
}
